import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ArrowLeftRight, CheckCircle2, ImageOff, PackageCheck } from 'lucide-react';
import useFetchUserAssets from '../../../hooks/DataFetching/useFetchUserAssets';

const TradeSelectionModal = ({ show, onHide, userId, onItemsSelected }) => {
    const inventory = useFetchUserAssets(userId, true);
    const inventoryItems = Array.isArray(inventory) ? inventory : [];
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
        if (!show) {
            setSelectedItems([]);
        }
    }, [show]);

    const handleItemClick = (item) => {
        if (selectedItems.includes(item)) {
            setSelectedItems(selectedItems.filter(i => i !== item));
        } else {
            setSelectedItems([...selectedItems, item]);
        }
    };

    const handleConfirmSelection = () => {
        onItemsSelected(selectedItems);
        onHide();
    };

    const isSelected = (item) => selectedItems.includes(item);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered contentClassName="marketplace-flow-modal">
            <Modal.Header closeButton className="marketplace-flow-header">
                <Modal.Title>
                    <span className="marketplace-flow-kicker"><ArrowLeftRight className="h-4 w-4" /> Trade offer</span>
                    Choose items to offer
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="marketplace-flow-body">
                <div className="marketplace-selection-summary">
                    <PackageCheck className="h-5 w-5" />
                    <span>{selectedItems.length} item{selectedItems.length === 1 ? '' : 's'} selected for this offer</span>
                </div>
                {inventoryItems.length === 0 ? (
                    <div className="marketplace-empty-selection">
                        <ImageOff className="h-8 w-8" />
                        <strong>No trade inventory available</strong>
                        <span>Add inventory items before making a trade offer.</span>
                    </div>
                ) : (
                    <div className="marketplace-trade-grid" aria-label="Your tradable inventory">
                        {inventoryItems.map((item, index) => {
                            const itemName = item.assetName || item.productName || 'Inventory item';
                            const itemDescription = item.assetDescription || item.productDescription || 'Available for trade consideration.';

                            return (
                                <button
                                    key={item.id || index}
                                    type="button"
                                    className={isSelected(item) ? 'marketplace-trade-card is-selected' : 'marketplace-trade-card'}
                                    onClick={() => handleItemClick(item)}
                                    aria-label={`${isSelected(item) ? 'Remove' : 'Add'} ${itemName} from this trade offer`}
                                    aria-pressed={isSelected(item)}
                                    title={itemName}
                                >
                                    <span className="marketplace-trade-card-media">
                                        {item.imageURL ? (
                                            <img src={item.imageURL} alt={itemName} />
                                        ) : (
                                            <ImageOff className="h-8 w-8" />
                                        )}
                                        <span className="marketplace-trade-media-caption">
                                            <strong>{itemName}</strong>
                                            <span>{isSelected(item) ? 'Selected for offer' : 'Tap to select'}</span>
                                        </span>
                                        {isSelected(item) && <CheckCircle2 className="marketplace-trade-selected-icon h-6 w-6" />}
                                    </span>
                                    <span className="marketplace-trade-card-body">
                                        <strong>{itemName}</strong>
                                        <span>{itemDescription}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="marketplace-flow-footer">
                <Button variant="outline-light" onClick={onHide} className="marketplace-secondary-action">
                    Cancel
                </Button>
                <Button
                    variant="success"
                    disabled={selectedItems.length === 0}
                    onClick={handleConfirmSelection}
                    className="marketplace-primary-action"
                >
                    Continue with {selectedItems.length} item{selectedItems.length === 1 ? '' : 's'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TradeSelectionModal;
